/**
 * The looks this frontend ships with, by the name an organisation's
 * `orgs.theme` selects (ADR 0010). `adm org theme --slug X --theme Y` assigns
 * one; `/api/session` reports it; `applyTheme` puts it on the page.
 *
 * A theme is code, not data: adding one is a pull request and a deploy, which
 * is where a customer's colours and wordmark ought to be reviewed. The server
 * keeps the same list of names in internal/branding/themes.go, and
 * TestThemesMatchTheFrontend fails if the two drift.
 *
 * Colours are hex here and become space-separated RGB channels on :root, so
 * Tailwind's opacity modifiers (`bg-muted/50`) keep working. app.css carries
 * the default theme's values as well, so the first paint before any script
 * runs is the default rather than unstyled.
 */

import twodogsLogo from "./assets/twodogs-logo.webp";
import twodogsIcon from "./assets/twodogs-icon.png";
import eabLogoReversed from "./assets/eab-erika-logo-reversed.png";
import eabSparkle from "./assets/eab-erika-sparkle.png";

export interface ThemeColors {
  background: string;
  foreground: string;
  border: string;
  borderStrong: string;
  input: string;
  primary: string;
  primaryHover: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  muted: string;
  mutedForeground: string;
  card: string;
  cardForeground: string;
  accent: string;
  accentForeground: string;
  /** The rule under the top navigation, drawn over the backdrop. */
  navRule: string;
  /**
   * Text and controls drawn straight onto the top of the backdrop: the
   * navigation, each page's title, the sign-in card. White over a dark
   * backdrop, ink over a light one.
   */
  chrome: string;
  /** The translucent panel behind chrome that needs one (sign-in card, org switcher). */
  chromeSurface: string;
}

export interface Theme {
  name: string;
  /** What the top bar, the sign-in card and the browser tab call the product. */
  productName: string;
  /**
   * The customer's wordmark, drawn in place of `productName` in the top bar
   * and on the sign-in card. Both sit on the dark top of the backdrop, so this
   * is the reversed (light-on-dark) artwork. Without one, the name is set in
   * the heading face.
   */
  logo?: string;
  /** The browser tab's icon. Without one, the tab shows the browser's own. */
  icon?: string;
  colors: ThemeColors;
  fonts: {
    sans: string;
    heading: string;
    bodyWeight: number;
    headingWeight: number;
    /** A Google Fonts stylesheet to load, when the faces are not already on the page. */
    stylesheet?: string;
  };
  /**
   * The page background behind every screen. The top of it sits under the
   * `chrome` colour — the navigation and each page's title — so the two have
   * to contrast there.
   */
  backdrop: string;
}

/**
 * Two Dogs AI, after twodogs.ai: charcoal ink on warm paper, taken from the
 * logo, with the site's one brown accent. Light chrome, because the logo is
 * line art drawn for paper and reads as a photographic negative inverted. The
 * site sets no web fonts, so neither does this: the system sans and the
 * old-style serif stack it uses for headings.
 */
const defaultTheme: Theme = {
  name: "default",
  productName: "Two Dogs AI",
  logo: twodogsLogo,
  icon: twodogsIcon,
  colors: {
    background: "#FAF9F5",
    foreground: "#262C2A",
    border: "#E2DFD4",
    borderStrong: "#D3CFC2",
    input: "#D3CFC2",
    primary: "#262C2A",
    primaryHover: "#874F20",
    primaryForeground: "#FAF9F5",
    secondary: "#FFFFFF",
    secondaryForeground: "#262C2A",
    muted: "#F2F0E8",
    mutedForeground: "#4D5552",
    card: "#FFFFFF",
    cardForeground: "#262C2A",
    accent: "#F1E4D6",
    accentForeground: "#874F20",
    navRule: "#E2DFD4",
    chrome: "#262C2A",
    chromeSurface: "#FFFFFFB3",
  },
  fonts: {
    sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    heading: '"Iowan Old Style", "Palatino Linotype", Palatino, "Book Antiqua", Georgia, serif',
    bodyWeight: 400,
    headingWeight: 600,
  },
  backdrop: "linear-gradient(180deg,#FAF9F5 0%,#F2F0E8 100%)",
};

/**
 * EAB, after eab.com: navy and congress blue over a cool grey, aqua for the
 * selected tab, and their yellow as the one bright line under the navigation.
 * eab.com sets Museo Sans and Museo Slab, which are licensed faces; Nunito Sans
 * and Roboto Slab are the nearest freely served ones. Swap them for Museo once
 * EAB gives us a font kit to load.
 */
const eabTheme: Theme = {
  name: "eab",
  productName: "ERIKA",
  // EAB's artwork is slate on transparent (eab-erika-logo.png, beside it);
  // the reversed copy swaps the slate for white and keeps their blue sparkle.
  logo: eabLogoReversed,
  // The sparkle from the same artwork, on its own: the wordmark is illegible at 16px.
  icon: eabSparkle,
  colors: {
    background: "#F7F7F9",
    foreground: "#031D32",
    border: "#DDE3EA",
    borderStrong: "#C9D2DC",
    input: "#C9D2DC",
    primary: "#004B87",
    primaryHover: "#00355F",
    primaryForeground: "#FFFFFF",
    secondary: "#FFFFFF",
    secondaryForeground: "#031D32",
    muted: "#EEF1F5",
    mutedForeground: "#4A5B6C",
    card: "#FFFFFF",
    cardForeground: "#031D32",
    accent: "#E0F1F1",
    accentForeground: "#00355F",
    navRule: "#F7CA0F",
    chrome: "#FFFFFF",
    chromeSurface: "#0000004D",
  },
  fonts: {
    sans: '"Nunito Sans", "Museo Sans", Helvetica, Arial, sans-serif',
    heading: '"Roboto Slab", "Museo Slab", Georgia, serif',
    bodyWeight: 400,
    headingWeight: 400,
    stylesheet:
      "https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;600;700&family=Roboto+Slab:wght@300;400;500&display=swap",
  },
  backdrop:
    "linear-gradient(180deg,#031D32 0%,#00355F 24%,#004B87 46%,#C9DCEA 70%,#F7F7F9 100%)",
};

export const THEMES: Record<string, Theme> = {
  default: defaultTheme,
  eab: eabTheme,
};

export const DEFAULT_THEME = defaultTheme;

/** The theme called `name`, or the default for a name this build does not have. */
export function themeNamed(name: string | null | undefined): Theme {
  return (name && THEMES[name]) || DEFAULT_THEME;
}

const CSS_VARS: Record<keyof ThemeColors, string> = {
  background: "--c-background",
  foreground: "--c-foreground",
  border: "--c-border",
  borderStrong: "--c-border-strong",
  input: "--c-input",
  primary: "--c-primary",
  primaryHover: "--c-primary-hover",
  primaryForeground: "--c-primary-foreground",
  secondary: "--c-secondary",
  secondaryForeground: "--c-secondary-foreground",
  muted: "--c-muted",
  mutedForeground: "--c-muted-foreground",
  card: "--c-card",
  cardForeground: "--c-card-foreground",
  accent: "--c-accent",
  accentForeground: "--c-accent-foreground",
  navRule: "--c-nav-rule",
  chrome: "--c-chrome",
  chromeSurface: "--c-chrome-surface",
};

/** "#2F6F62" → "47 111 98"; an eight-digit hex keeps its alpha as "/ 0.15". */
function channels(hex: string): string {
  const h = hex.replace("#", "");
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  if (h.length === 8) {
    const a = Math.round((parseInt(h.slice(6, 8), 16) / 255) * 100) / 100;
    return `${r} ${g} ${b} / ${a}`;
  }
  return `${r} ${g} ${b}`;
}

const STORAGE_KEY = "2dai.theme";

/**
 * Puts a theme on the page and remembers it, so the next visit — including the
 * sign-in screen, before there is a session to ask — opens in the same look.
 */
export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  for (const [key, cssVar] of Object.entries(CSS_VARS) as [keyof ThemeColors, string][]) {
    root.style.setProperty(cssVar, channels(theme.colors[key]));
  }
  root.style.setProperty("--font-sans", theme.fonts.sans);
  root.style.setProperty("--font-heading", theme.fonts.heading);
  root.style.setProperty("--font-weight-body", String(theme.fonts.bodyWeight));
  root.style.setProperty("--font-weight-heading", String(theme.fonts.headingWeight));
  root.style.setProperty("--backdrop", theme.backdrop);
  root.dataset.theme = theme.name;

  if (theme.fonts.stylesheet && !document.querySelector(`link[data-theme-fonts="${theme.name}"]`)) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = theme.fonts.stylesheet;
    link.dataset.themeFonts = theme.name;
    document.head.appendChild(link);
  }

  document.title = `${theme.productName} — Research assistant`;

  // One icon link, owned here. Switching to a theme without an icon removes
  // it, rather than leaving the last org's mark on another org's tab.
  let icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (theme.icon) {
    if (!icon) {
      icon = document.createElement("link");
      icon.rel = "icon";
      document.head.appendChild(icon);
    }
    icon.href = theme.icon;
  } else {
    icon?.remove();
  }

  try {
    localStorage.setItem(STORAGE_KEY, theme.name);
  } catch {
    // Private windows and blocked storage: the theme still applies, it just
    // is not remembered for the sign-in screen next time.
  }
}

/** The theme this browser last showed, for use before a session exists. */
export function rememberedTheme(): Theme {
  try {
    return themeNamed(localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_THEME;
  }
}
